import { observable, action, configure, makeObservable } from 'mobx';
import APIStage from '@api/graphqlStageOld';
import { IEdetector } from '@utils/browserUTLs';
import CartStore from './CartStore';
import {
  configValuesKeys,
  type ConfigValuesKey
} from '../api/enums/configValues.enum';
import type { ConfigData } from './ConfigStore.type';

/**
 * Приводит сырое значение конфига с бэка к типу из ConfigData. Общий для
 * wakeUp и __wakeUp, чтобы оба пути давали одинаковые данные.
 * value может прийти не строкой — поэтому String(value).
 */
const parseConfigValue = (
  key: ConfigValuesKey,
  value: unknown
): ConfigData[keyof ConfigData] => {
  if (key === 'invite_acl') {
    return String(value)
      .split(',')
      .map(item => +item);
  }
  if (key === 'force_yandex_metrika') {
    return String(value).trim() === '1';
  }
  return value as string;
};

configure({
  enforceActions: 'observed',
  useProxies: IEdetector() ? 'never' : 'always'
});

class Store {
  data: ConfigData = {};

  constructor() {
    makeObservable(this, {
      data: observable,
      setValues: action,
      wakeUp: action,
      takeDeliveryValues: action
    });
  }

  async wakeUp(): Promise<void> {
    await APIStage.configValueList(configValuesKeys).then(result => {
      const data = {};

      configValuesKeys.forEach(a => {
        const value = result?.[a]?.value;

        if (value) {
          data[a] = parseConfigValue(a, value);
        }
      });

      // data.invite_bonus_invited = '500';
      // data.invite_bonus_inviter = '555';
      this.setValues(data);

      CartStore.setConfigFromConfigStore(data);
    });
  }

  async __wakeUp(): Promise<void> {
    await Promise.all(
      configValuesKeys.map(
        async a =>
          await APIStage.configValue({
            name: a
          })
      )
    ).then(result => {
      const data = {};

      configValuesKeys.forEach((a, i) => {
        const value = result?.[i]?.configValue?.value;

        if (value) {
          data[a] = parseConfigValue(a, value);
        }
      });
      this.setValues(data);
    });
  }

  setValues(data: ConfigData): void {
    this.data = data;
  }

  getValue<T extends keyof ConfigData, K>(
    name: T,
    defaultValue?: K extends ConfigData[T] ? K : never
  ): ConfigData[T] | null {
    if (typeof this.data[name] === 'undefined' && defaultValue) {
      return defaultValue;
    }
    // ЭТАП 2 (SSR): глобального location на сервере нет — обращение к нему
    // прямо в рендере роняло страницы, которые спрашивают ключ карты
    // (ShopsMap на /contacts, /shop, лендингах). На сервере ключа просто нет,
    // карта там всё равно client-only.
    if (
      name === 'api_yandex_key' &&
      typeof window !== 'undefined' &&
      !window.location.href.includes('delikateska.ru')
    ) {
      const devApiKey = '23250f83-4a82-49eb-a14c-33d3b2af9f45' as ConfigData[T];
      return devApiKey;
    }
    if (!this.data) {
      return null;
    }

    return this.data[name];
  }

  takeDeliveryValues = (): Partial<
    Record<
      'morningStart' | 'morningEnd' | 'eveningStart' | 'eveningEnd',
      string | null | undefined
    >
  > => {
    const {
      default_delivery_morning_start,
      default_delivery_morning_end,
      default_delivery_evening_start,
      default_delivery_evening_end
    } = this.data;
    return CartStore.setConfigFromConfigStore({
      default_delivery_morning_start,
      default_delivery_morning_end,
      default_delivery_evening_start,
      default_delivery_evening_end
    });
  };
}

const configStore = new Store();
export type ConfigStoreType = typeof configStore;
export default configStore;
